import React, { useState, useEffect } from 'react';
import { Bot, Sparkles, Key, Save, CheckCircle, AlertCircle, Eye, EyeOff, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from '../hooks/use-toast';
import AIFallbackSettings from './AIFallbackSettings';
import AIAvisosFallos from './AIAvisosFallos';
import AIModulosProveedor from './AIModulosProveedor';
import { aiSettingsAPI } from '../api';
import { AI_PROVIDERS, CHATGPT_CONFIG, GEMINI_CONFIG, SYSTEM_PROMPTS, DEFAULT_AI_CONFIG } from '../config/aiProviders';

const AISettings = () => {
  const [settings, setSettings] = useState({
    provider: DEFAULT_AI_CONFIG.provider,
    geminiApiKey: '',
    systemPromptKey: 'legal_general',
    customSystemPrompt: '',
    useCustomPrompt: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await aiSettingsAPI.get();
      if (response.data) {
        setSettings(prev => ({
          ...prev,
          ...response.data,
          geminiApiKey: response.data.geminiApiKey || ''
        }));
      }
    } catch (error) {
      console.error('Error cargando configuración:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await aiSettingsAPI.update(settings);
      toast({ title: "Configuración guardada", description: "Los ajustes del asistente IA se han guardado correctamente" });
    } catch (error) {
      toast({ title: "Error", description: "No se pudo guardar la configuración", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await aiSettingsAPI.test({
        provider: settings.provider,
        geminiApiKey: settings.geminiApiKey,
        message: "Hola, ¿puedes confirmar que estás funcionando correctamente?"
      });
      setTestResult({ success: true, message: response.data.response?.substring(0, 200) + "..." });
      toast({ title: "Test exitoso", description: "El asistente IA está funcionando correctamente" });
    } catch (error) {
      setTestResult({ success: false, message: error.response?.data?.detail || "Error de conexión" });
      toast({ title: "Error en test", description: "No se pudo conectar con el proveedor de IA", variant: "destructive" });
    } finally {
      setTesting(false);
    }
  };

  const currentPrompt = settings.useCustomPrompt 
    ? settings.customSystemPrompt 
    : SYSTEM_PROMPTS[settings.systemPromptKey]?.prompt || '';

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Selector de Proveedor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600" />
            Proveedor de IA
          </CardTitle>
          <CardDescription>Selecciona el modelo de inteligencia artificial para el asistente legal</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* ChatGPT Option */}
            <div 
              onClick={() => setSettings({...settings, provider: AI_PROVIDERS.CHATGPT})}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                settings.provider === AI_PROVIDERS.CHATGPT 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold">{CHATGPT_CONFIG.displayName}</h3>
                  <Badge variant="outline" className="text-xs">Recomendado</Badge>
                </div>
              </div>
              <p className="text-sm text-gray-600">{CHATGPT_CONFIG.description}</p>
              {settings.provider === AI_PROVIDERS.CHATGPT && (
                <div className="mt-3 p-2 bg-blue-100 rounded-lg text-xs text-blue-700">
                  ✓ Usa la Emergent LLM Key incluida en la plataforma
                </div>
              )}
            </div>

            {/* Gemini Option */}
            <div 
              onClick={() => setSettings({...settings, provider: AI_PROVIDERS.GEMINI})}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                settings.provider === AI_PROVIDERS.GEMINI 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Bot className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold">{GEMINI_CONFIG.displayName}</h3>
                  <Badge variant="outline" className="text-xs">Búsqueda Web</Badge>
                </div>
              </div>
              <p className="text-sm text-gray-600">{GEMINI_CONFIG.description}</p>
            </div>
          </div>

          {/* Gemini API Key */}
          {settings.provider === AI_PROVIDERS.GEMINI && (
            <div className="mt-4 p-4 bg-purple-50 rounded-xl border border-purple-200">
              <Label className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4" />
                API Key de Google Gemini
              </Label>
              <div className="relative">
                <Input
                  type={showApiKey ? "text" : "password"}
                  value={settings.geminiApiKey}
                  onChange={(e) => setSettings({...settings, geminiApiKey: e.target.value})}
                  placeholder="AIzaSy..."
                  className="pr-10"
                />
                <button 
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Obtén tu API key en <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">Google AI Studio</a>
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Prompt */}
      <Card>
        <CardHeader>
          <CardTitle>Instrucciones del Sistema</CardTitle>
          <CardDescription>Define el comportamiento y especialización del asistente</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Plantilla de especialización</Label>
            <Select 
              value={settings.useCustomPrompt ? 'custom' : settings.systemPromptKey}
              onValueChange={(value) => {
                if (value === 'custom') {
                  setSettings({...settings, useCustomPrompt: true});
                } else {
                  setSettings({...settings, systemPromptKey: value, useCustomPrompt: false});
                }
              }}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SYSTEM_PROMPTS).map(([key, value]) => (
                  <SelectItem key={key} value={key}>{value.name}</SelectItem>
                ))}
                <SelectItem value="custom">Personalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Prompt del sistema {settings.useCustomPrompt && '(Personalizado)'}</Label>
            <Textarea
              value={currentPrompt}
              onChange={(e) => setSettings({...settings, customSystemPrompt: e.target.value, useCustomPrompt: true})}
              placeholder="Define las instrucciones para el asistente..."
              rows={8}
              className="mt-1 font-mono text-sm"
              readOnly={!settings.useCustomPrompt}
            />
            {!settings.useCustomPrompt && (
              <p className="text-xs text-gray-500 mt-1">Selecciona "Personalizado" para editar el prompt</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Test & Save */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              variant="outline" 
              onClick={handleTest}
              disabled={testing || (settings.provider === AI_PROVIDERS.GEMINI && !settings.geminiApiKey)}
              className="flex-1"
            >
              {testing ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Probando...</>
              ) : (
                <><Bot className="w-4 h-4 mr-2" />Probar Conexión</>
              )}
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />Guardar Configuración</>
              )}
            </Button>
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`mt-4 p-4 rounded-lg ${
              testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start gap-2">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                )}
                <div>
                  <p className={`font-medium ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.success ? 'Conexión exitosa' : 'Error de conexión'}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">{testResult.message}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Independencia / Fallback de proveedor IA */}
      <AIFallbackSettings />

      {/* Qué proveedor usa cada función */}
      <AIModulosProveedor />

      {/* Aviso por correo cuando la IA deja de responder + prueba de caída */}
      <AIAvisosFallos />
    </div>
  );
};

export default AISettings;
